import { makeObservable, observable, runInAction } from 'mobx';
import { BaseStore } from './baseStore';
import { RootStore } from './rootStore';
import { AdjustmentService } from '/src/api';
import { Adjustment, TAdjustmentId } from '/src/lib/models';
import {
  CreateAdjustmentForm,
  MenuItemType,
  TCreateAdjustmentRequest,
  TListAdjustmentsRequest,
  TUpdateAdjustmentRequest,
  UpdateAdjustmentForm,
  UserRole,
} from '/src/lib/types';

export interface AdjustmentStoreProps {
  /**
   * List of Adjustments
   */
  adjustments?: Adjustment[];
  /**
   * Currently selected Adjustment
   */
  currentAdjustment?: Adjustment;
}

export class AdjustmentStore extends BaseStore implements AdjustmentStoreProps {
  private didInitMenu = false;

  adjustments?: Adjustment[];
  currentAdjustment?: Adjustment;

  constructor(rootStore: RootStore) {
    super(rootStore);

    makeObservable(this, {
      adjustments: observable,
      currentAdjustment: observable,
    });
  }

  /**
   * Adds Adjustment nav menu items to the app header.
   */
  initMenuItems = () => {
    if (this.didInitMenu || !this.rootStore.userStore.user) return;

    // Add menu items
    this.rootStore.menuStore.addMenuItem(
      'topbar',
      'Adjustments',
      'adjustments',
      this.rootStore.userStore.isLender ? MenuItemType.Item : MenuItemType.Dropdown,
      '/adjustments(/create)?',
      false,
      undefined,
      10
    );
    if (!this.rootStore.userStore.isLender) {
      this.rootStore.menuStore.addSubMenuItem('topbar', 'adjustments', 'List Adjustments', 'adjustments');
      this.rootStore.menuStore.addSubMenuItem(
        'topbar',
        'adjustments',
        'New Adjustment',
        'adjustments/create',
        'adjustments/create',
        false,
        [UserRole.Admin, UserRole.Company]
      );
    }

    runInAction(() => (this.didInitMenu = true));
  };

  /**** Data Processing ****/

  /**
   * Formats List Adjustments responses.
   *
   * @param adjustments - An array of Adjustments to format
   * @returns The formatted Adjustments
   */
  formatAdjustmentList = (adjustments: Adjustment[]) => {
    return adjustments.map((adjustment) => this.formatAdjustment(adjustment));
  };

  /**
   * Format Get Adjustment responses.
   *
   * @param adjustment - The Adjustment to format
   * @returns The formatted Adjustment
   */
  formatAdjustment = (adjustment: Adjustment) => {
    // Calculate
    adjustment.resultingAssetValue = (adjustment.assetValue ?? 0) + adjustment.amount;

    // Parse
    adjustment.effectiveDate = new Date(adjustment.effectiveDate);

    return adjustment;
  };

  /**** Adjustment CRUD ****/

  /**
   * Fetches a list of Adjustments.
   *
   * @param query - Optional query to send with the API request
   * @returns An array of Adjustments
   */
  query = async (query?: TListAdjustmentsRequest) => {
    // if (query) query.px = true;
    const response = await AdjustmentService.query(query);

    runInAction(() => {
      this.adjustments = this.formatAdjustmentList(response);
    });

    return this.adjustments;
  };

  /**
   * Fetches a single Adjustment and sets it as the current Adjustment.
   *
   * @param adjustmentId - ID of the Adjustment to fetch
   * @returns An Adjustment object
   */
  get = async (adjustmentId: TAdjustmentId) => {
    const response = await AdjustmentService.get(adjustmentId);

    runInAction(() => {
      this.currentAdjustment = response ? this.formatAdjustment(response) : undefined;
    });

    return this.currentAdjustment;
  };

  /**
   * Creates an Adjustment.
   *
   * @param formValues - A populated CreateAdjustmentForm object
   * @returns The new Adjustment
   */
  create = async (formValues: CreateAdjustmentForm) => {
    if (
      !formValues.company ||
      !formValues.loan ||
      !formValues.name ||
      !formValues.asset ||
      !formValues.effectiveDate ||
      Number.isNaN(formValues.amount)
    ) {
      throw new Error('Please fix the errors first.');
    }

    // Create new Adjustment object
    const adjustment: TCreateAdjustmentRequest = {
      company: formValues.company._id,
      loan: formValues.loan._id,
      effectiveDate: formValues.effectiveDate,
      name: formValues.name,
      assetId: formValues.asset._id,
      assetValue: formValues.asset.value,
      amount: formValues.amount as unknown as number,
      notes: formValues.notes,
      files: formValues.files,
      user: this.rootStore.userStore.user,
    };

    const response = await AdjustmentService.create(adjustment);

    return response;
  };

  /**
   * Updates an Adjustment.
   *
   * @param formValues - A populated UpdateAdjustmentForm object
   * @returns The updated Adjustment
   */
  update = async (formValues: UpdateAdjustmentForm) => {
    const adjustment: TUpdateAdjustmentRequest = formValues;

    const response = await AdjustmentService.update(adjustment._id, adjustment);

    runInAction(() => (this.currentAdjustment = Object.assign(this.currentAdjustment ?? {}, response)));

    return this.currentAdjustment;
  };

  /**
   * Removes an Adjustment.
   *
   * @param adjustmentId - ID of the Adjustment to delete
   * @returns The removed Adjustment
   */
  remove = async (adjustmentId: TAdjustmentId) => {
    const removed = await AdjustmentService.remove(adjustmentId);
    runInAction(() =>
      this.adjustments?.splice(
        this.adjustments?.findIndex((adjustment) => adjustment._id === adjustmentId),
        1
      )
    );

    return removed;
  };
}
